import React from "react";

import { getIn, useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { Label, OptionTogglerField } from "components/Form/FormikControls";
import { withNamespace } from "components/Form/FormikControls/lib";
import { Column, Row } from "components/Layout";
import { CombinedFormikMissingFieldIndicator } from "components/MissingFieldsIndicator";
import { BoldText } from "components/Text";

import { EMPTY_ARRAY } from "lib";

import { getCurrentSaleSubType, getWeightRangeBySaleRoundId } from "selectors";

export const EstAvgWeightRangeField = ({
  name,
  ns = "",
  saleRoundIdField = "sale_round_id",
  disabled,
  showNeedsRoundMessage = false,
  showNoValuesMessage = false,
  missingRoundMessage = "Select a Sale Round to see available Est Avg Weights.",
  modelIds = EMPTY_ARRAY,
  selectedSaleSubType = null,
}) => {
  const formik = useFormikContext();
  const { setFieldValue, values } = formik;

  const namespacedName = withNamespace(ns, name);

  const saleRoundId = getIn(values, saleRoundIdField);

  const weightRanges = useSelector(getWeightRangeBySaleRoundId(saleRoundId));

  const currentSaleSubType = useSelector(getCurrentSaleSubType);

  const saleSubType = selectedSaleSubType || currentSaleSubType;

  const saleSubTypeId = saleSubType?.id;

  const mappedWeightRanges = weightRanges.map(range => {
    return {
      label: range.shortCode,
      value: range.id,
    };
  });

  const weightRangeToolTip = (
    <div>
      {weightRanges.map(range => {
        let toolTipText = `${range.shortCode} - ${range.details} - ${range.weightRangeMinKg}- ${range.weightRangeMaxKg}Kg`;
        if (!range.weightRangeMaxKg) {
          toolTipText = `${range.shortCode} - ${range.details} - ${range.weightRangeMinKg}+ Kg`;
        }
        return (
          <BoldText key={range.id}>
            {toolTipText}
            <br />
          </BoldText>
        );
      })}
    </div>
  );

  const currentWeightRange = weightRanges.find(
    range => range.id === getIn(values, namespacedName),
  );

  const currentWeightRangeMatchesRound =
    currentWeightRange?.saleRoundId === saleRoundId;

  const currentWeightRangeMatchesSubType =
    currentWeightRange?.saleSubType === saleSubTypeId;

  React.useEffect(() => {
    if (!currentWeightRangeMatchesRound) {
      setFieldValue(namespacedName, null);
    }
    if (!currentWeightRangeMatchesSubType) {
      setFieldValue(namespacedName, null);
    }
  }, [
    namespacedName,
    setFieldValue,
    currentWeightRangeMatchesRound,
    currentWeightRangeMatchesSubType,
  ]);

  if (mappedWeightRanges.length > 0) {
    return (
      <OptionTogglerField
        labelPosition="top"
        name={namespacedName}
        label="Est Avg Weight (+/- 2Kg)"
        options={mappedWeightRanges}
        disabled={disabled}
        tooltip={weightRangeToolTip}
        showMissingFieldWarnings
        missingNASFieldWarningEnabled
        modelIds={modelIds}
        overrideMissingFieldName="Weight Range"
      />
    );
  } else if (
    (mappedWeightRanges.length === 0 && showNoValuesMessage) ||
    (showNeedsRoundMessage && !saleRoundId)
  ) {
    return (
      <Column>
        <Row justifyBetween>
          <Label>Est Avg Weight (+/- 2Kg)</Label>
          <CombinedFormikMissingFieldIndicator
            missingMLAFieldsWarningEnabled={false}
            missingNASFieldWarningEnabled
            overrideMissingFieldName="Weight Range"
            name={namespacedName}
            modelIds={modelIds}
          />
        </Row>
        <div>
          {showNeedsRoundMessage && !saleRoundId
            ? missingRoundMessage
            : "No Weight Range Options are configured."}
        </div>
      </Column>
    );
  } else {
    return null;
  }
};
